import type { ProductManifest } from './manifest'

/**
 * The product registry: an ordered, validated set of manifests the shell
 * iterates. Built once at module init (an invalid combination is a build
 * defect, so it throws, loudly, at construction — not at render).
 */
export interface ProductRegistry<M extends ProductManifest<never>> {
  /** Every registered manifest, in display order. */
  manifests: readonly M[]
  /** The registered ids, in the same order. */
  ids: readonly string[]
  /** Look a manifest up by id; `undefined` for an unknown id. */
  get(id: string): M | undefined
  /** Whether `id` is registered. */
  has(id: string): boolean
  /**
   * Whether the integration between products `a` and `b` is active for a
   * session: both REGISTERED here and both in `entitled` (D9 — cross-product
   * UI renders only when both sides are present and entitled).
   */
  integrationActive(a: string, b: string, entitled: Iterable<string>): boolean
}

/**
 * Build a registry from manifests, in display order.
 *
 * Validates at construction:
 * - ids are unique (a duplicate is two products claiming one entitlement key);
 * - every `requires` names a registered product (D9 hard dependency — the
 *   shell refuses the combination rather than rendering a product whose
 *   dependency is absent).
 *
 * @throws Error on a duplicate id or an unmet `requires`.
 */
export function createProductRegistry<M extends ProductManifest<never>>(
  manifests: readonly M[],
): ProductRegistry<M> {
  const byId = new Map<string, M>()
  for (const m of manifests) {
    if (byId.has(m.id)) throw new Error(`Duplicate product manifest id "${m.id}"`)
    byId.set(m.id, m)
  }
  for (const m of manifests) {
    for (const req of m.requires ?? []) {
      if (!byId.has(req)) {
        throw new Error(
          `Product "${m.id}" requires "${req}", which is not registered — refuse the combination (D9)`,
        )
      }
    }
  }
  return {
    manifests,
    ids: manifests.map((m) => m.id),
    get: (id) => byId.get(id),
    has: (id) => byId.has(id),
    integrationActive(a, b, entitled) {
      if (!byId.has(a) || !byId.has(b)) return false
      const set = entitled instanceof Set ? (entitled as Set<string>) : new Set(entitled)
      return set.has(a) && set.has(b)
    },
  }
}
